import { Box, TextField, MenuItem, Divider, Checkbox } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, {ChangeEventHandler, useMemo} from "react";
import { connect } from "react-redux";
import { selectConstants } from "../../../redux/selectors/firebase";
import { Constants } from "../../graph/StateTags";
import {ColumnDetails, ConstantColumnData, getColumnDetails} from "../gridColumnDefs";
import { Sheet } from "../GridView";

export const typeOperators =
{
    any: {
        empty: "Is empty",
        notEmpty: "Is not empty",
    },
    number: {
        ">": "Greater than",
        ">=": "Greater than or equal to",
        "<": "Less than",
        "<=": "Less than or equal to",
        "==": "Is equal to",
        "!=": "Is not equal to"
    },
    boolean: {
        "booleanIs": "Is"
    },
    date: {
        "dateIs": "Is",
        "dateIsBefore": "Is before",
        "dateIsAfter": "Is after",
        "dateIsBeforeColumn": "Is before column",
        "dateIsAfterColumn": "Is after column"
    },
    dateTime: {
        "dateIs": "Is",
        "dateIsBefore": "Is before",
        "dateIsAfter": "Is after",
        "dateIsBeforeColumn": "Is before column",
        "dateIsAfterColumn": "Is after column"
    },
    string: {
        "stringContains": "Text contains",
        "stringNotContains": 'Text does not contain',
        "stringStartsWith": "Text starts with",
        "stringEndsWith": "Text ends with",
        "stringEquals": "Text is Exactly"
    },
    singleSelect: {
        "singleSelectIs": "Is"
    }
}

export function getFormatRuleName(id: string)
{
    const entry = Object.entries(typeOperators).find(([category, ruleEntries]) =>
    {
        return id in ruleEntries;
    });
    if (entry)
    {
        const [, ruleEntry] = entry;
        return (ruleEntry as any)[id];
    }
    return "";
}

type KeyOfTypeOperator = keyof typeof typeOperators

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        alignItems: "baseline"
    },
    textField: {
        flexGrow: 1
    }
}));


export interface RuleDefinition
{
    columnId: string,
    operator: string,
    value?: any
}

interface Props
{
    sheet: Sheet
    constants: Constants

    rule: RuleDefinition,
    onRuleChanged: (rule: RuleDefinition) => void
}

function FormatRule({ sheet, constants, rule, onRuleChanged }: Props)
{
    const classes = useStyles();
    const selectedColumnDetails: ColumnDetails | null = useMemo(() =>
    {
        const columnEntry = Object.entries(sheet.columns ?? {}).find(([id, column]) => id === rule.columnId);
        if (columnEntry)
        {
            return getColumnDetails(columnEntry, constants);
        }

        return null
    }, [constants, rule, sheet]);

    const createEntries = (key: string) =>
    {
        const entries = typeOperators[key as KeyOfTypeOperator];
        return [<Divider key={key} />].concat(Object.entries(entries).map(([id, text]) =>
        {
            return <MenuItem key={id} value={id}>{text}</MenuItem>
        }));
    }

    const hasValue = (operator: string) =>
    {
        const typesWithValue: KeyOfTypeOperator[] = [
            "number",
            "string",
            "boolean",
            "date",
            "dateTime",
            "singleSelect"
        ];
        return typesWithValue.some((type) => operator in typeOperators[type])
    }

    const valueInput = () =>
    {
        const handleUpdateDate: ChangeEventHandler<HTMLInputElement> = ({currentTarget}) => {
            const date = new Date(currentTarget.value);

            onRuleChanged({...rule, value: date.getTime()})
        };
        const handleUpdateColumn: ChangeEventHandler<HTMLInputElement> = ({target}) =>
            onRuleChanged({...rule, value: target.value});

        if (selectedColumnDetails)
        {
            switch (selectedColumnDetails.type)
            {
                case "boolean":
                    return <Checkbox autoFocus
                        checked={rule.value ?? false}
                        onChange={(e) => onRuleChanged({ ...rule, value: e.currentTarget.checked })} />
                case "singleSelect":
                    return <TextField
                        label={"Value"}
                        select
                        className={classes.textField}
                        SelectProps={{
                            value: rule.value < (selectedColumnDetails.valueOptions?.length ?? 0) ? rule.value : 0,
                            onChange: (e) =>
                            {
                                onRuleChanged({ ...rule, value: e.target.value });
                            }
                        }}>
                        {Object.entries(selectedColumnDetails.valueOptions ?? {}).map(([id, entry]) =>
                        {
                            return <MenuItem key={id} value={id}>{entry}</MenuItem>
                        })}
                    </TextField>
                case "date":
                case "datetime":
                    if (rule.operator === "dateIsBeforeColumn" || rule.operator === "dateIsAfterColumn") {
                        const options: {[key: string]: string} = Object.fromEntries(Object.entries(sheet.columns ?? {})
                            .map(([key, column]) => {
                                const constantColumnData = column as ConstantColumnData;

                                if (constantColumnData.source) {
                                    const constant = constants[constantColumnData.source];

                                    return [constantColumnData.source, constant?.name]
                                }
                                return [key, undefined];
                            })
                            .filter(([key]) => key !== (sheet.columns[rule.columnId] as ConstantColumnData).source)
                            .filter(([, name]) => name !== undefined));
                        const value = options[rule.value] ? rule.value : "";

                        return (
                            <TextField fullWidth label="Compare" onChange={handleUpdateColumn} select size="small"
                                       value={value}>
                                <MenuItem value="" disabled>Select Column</MenuItem>
                                {Object.entries(options).map(([key, name]) =>
                                    <MenuItem key={key} value={key}>{name}</MenuItem>
                                )}
                            </TextField>
                        );
                    } else {
                        const date = Number.isInteger(rule.value) ? new Date(rule.value).toISOString().substring(0, 16) : "";

                        return (
                            <TextField InputLabelProps={{ shrink: true }} label="Value" variant="outlined" size="small"
                                       margin="dense" type="datetime-local" fullWidth value={date}
                                       onChange={handleUpdateDate} />
                        );
                    }
                default:
                    return <TextField autoFocus label="Value" className={classes.textField}
                                      type={selectedColumnDetails?.type} value={rule.value ?? ""} 
                                      onChange={(e) => onRuleChanged({
                                          ...rule,
                                          value: e.currentTarget.value
                                      })} />
            }
        }
        return null;
    }

    const columnEntries = useMemo(() => Object.entries(sheet.columns ?? {}).filter((([id, column]) => column.columnType !== "auto")), [sheet]);

    return <Box className={classes.container}>
        <TextField label={"Column"} select className={classes.textField} size="small" SelectProps={{
                value: rule.columnId,
                onChange: (e) =>
                {
                    const id = Object.keys(sheet.columns ?? {}).find((id) => id === e.target.value);
                    if (id)
                    {
                        onRuleChanged({ columnId: id, operator: "empty", value: null });
                    }
                }
            }}>
            {columnEntries.map(([id, column]) =>
            {
                const details = getColumnDetails([id, column], constants);
                return <MenuItem key={`column_${id}`} value={id}>
                    {details?.icon ? React.createElement(details.icon) : null}{details?.name}
                </MenuItem>
            })}
        </TextField>

        {selectedColumnDetails &&
            <TextField label={"Rule"} select className={classes.textField} size="small" SelectProps={{
                value: rule.operator,
                onChange: (e) =>
                {
                    const operator = e.target.value as string;
                    onRuleChanged({ ...rule, operator, value: hasValue(operator) ? rule.value : null });
                }
            }}>
            {createEntries("any")}
            {selectedColumnDetails.type in typeOperators
                && createEntries(selectedColumnDetails.type)}
        </TextField>}

        {hasValue(rule.operator) && selectedColumnDetails && React.createElement(valueInput)}
    </Box >
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) ?? {}
});
export default connect(mapStateToProps, {
})(FormatRule);